import {
  createIdempotencyKey,
  type PaymentEvidence,
  type StaffRefundAnswer,
} from "@lafayette/api-client";
import type { StaffRefundBody } from "@lafayette/schemas";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { hintClass } from "../../requests/components/parts";
import { Amount } from "../../ui/amount";
import { Button } from "../../ui/button";
import { Dialog } from "../../ui/dialog";
import { focusFirstInvalid, SelectField, TextAreaField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { describePaymentError, type PaymentFailure } from "../errors";
import { formatMinor, minorToRupeeText, parseRupeesToMinor } from "../format";
import { evidenceMeanings, refundReasonOptions } from "../labels";
import { usePaymentsRuntime } from "../runtime";
import { EvidenceLabel, PaymentFailureNotice } from "./parts";

// The refund dialog (CHT-759). A refund moves money, so it is the most careful screen of the console:
//   - It is for a supervisor or an administrator (the page does not draw the button for anyone else; the API refuses them anyway).
//   - The amount is typed in rupees and read from the TEXT into integer paise (`parseRupeesToMinor`): no float touches it. It cannot be
//     more than what is left to refund when the screen knows that; the server and the database bound it again.
//   - A second step confirms it in words: the amount formatted from the integer paise, the total, the provider and the FAKE or REAL label.
//   - ONE Idempotency-Key lives as long as this dialog. After a send that may have run (no answer, a busy provider, a `202`) the fields are
//     LOCKED and "Try again" sends the same refund with the same key, so the provider is asked once. A new dialog is a new key.
//   - Escape and Cancel do nothing while the refund is being sent: the dialog that is closed could not receive its own answer.
//   - The status of the payment changes only when the provider confirms the refund. This dialog never says "refunded".

const REFUND_REASON_MAX = 500;

type Props = Readonly<{
  paymentRequestId: string;
  /** The amount of the payment, in paise, when the screen read it (an administrator cannot read it: `null`). */
  totalMinor: number | null;
  /** What is left to refund, in paise, when known. */
  remainingMinor: number | null;
  currency: string;
  evidence: PaymentEvidence | null;
  onDone: (answer: StaffRefundAnswer & { kind: "RESULT" }) => void;
  onClose: () => void;
}>;

export function RefundDialog({
  paymentRequestId,
  totalMinor,
  remainingMinor,
  currency,
  evidence,
  onDone,
  onClose,
}: Props) {
  const { payments } = usePaymentsRuntime();
  const [amountText, setAmountText] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<
    Readonly<{ amount?: string; reasonCode?: string; reason?: string }>
  >({});
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [failure, setFailure] = useState<PaymentFailure | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [pending, setPending] = useState(false);
  // A ref as well as the state: Escape can arrive before the next render shows the pending state.
  const sendingRef = useRef(false);
  // ONE key for this dialog. It is made once and never changes while the dialog lives.
  const keyRef = useRef<string | null>(null);
  keyRef.current ??= createIdempotencyKey();
  // Once a send was attempted, the fields are locked so a retry is the SAME refund.
  const [locked, setLocked] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (attempt > 0) focusFirstInvalid(form.current);
  }, [attempt]);

  const terminal = failure !== null && !failure.retryable;
  const amountMinor = parseRupeesToMinor(amountText);

  function validate(): boolean {
    const next: { amount?: string; reasonCode?: string; reason?: string } = {};
    if (amountMinor === null) {
      next.amount = "Type an amount above zero, in rupees, with at most two decimals.";
    } else if (remainingMinor !== null && amountMinor > remainingMinor) {
      next.amount = `You can refund at most ${formatMinor(remainingMinor, currency)}.`;
    }
    if (reasonCode === "") next.reasonCode = "Choose a reason.";
    const trimmed = reason.trim();
    if (trimmed === "") next.reason = "Write a reason.";
    else if (trimmed.length > REFUND_REASON_MAX) {
      next.reason = `Use at most ${REFUND_REASON_MAX} characters.`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function toConfirm(event: FormEvent) {
    event.preventDefault();
    if (validate()) setStep("confirm");
    else setAttempt((count) => count + 1);
  }

  async function send() {
    if (sendingRef.current || terminal || amountMinor === null) return;
    sendingRef.current = true;
    setInFlight(true);
    setLocked(true);
    setFailure(null);
    try {
      const answer = await payments.refund(
        paymentRequestId,
        {
          amountMinor,
          reasonCode: reasonCode as StaffRefundBody["reasonCode"],
          reason: reason.trim(),
        },
        { idempotencyKey: keyRef.current as string },
      );
      if (answer.kind === "PENDING") {
        // The provider call may still be running. The same refund can be asked about again with the same key.
        setPending(true);
        return;
      }
      setPending(false);
      onDone(answer);
    } catch (error) {
      setPending(false);
      setFailure(describePaymentError(error, "refund"));
    } finally {
      sendingRef.current = false;
      setInFlight(false);
    }
  }

  // No way out while the refund is being sent (Escape, Cancel).
  const guardedClose = () => {
    if (!sendingRef.current) onClose();
  };

  const evidenceNote =
    evidence === null ? null : (
      <p className={`${hintClass} flex flex-wrap items-center gap-1.5`}>
        <EvidenceLabel evidence={evidence} /> {evidenceMeanings[evidence]}
      </p>
    );

  return (
    <Dialog
      title="Refund this payment"
      description="A refund sends money back to the customer through the payment provider. The payment shows as refunded only after the provider confirms it."
      onClose={guardedClose}
    >
      <form
        ref={form}
        noValidate
        onSubmit={step === "form" ? toConfirm : (event) => event.preventDefault()}
        className="grid gap-4"
        aria-label="Refund this payment"
      >
        {evidenceNote}
        {step === "form" ? (
          <>
            {totalMinor === null ? (
              <p className={hintClass}>
                Your role can refund a payment but cannot read it, so the amount left to refund is
                not shown here. The server refuses an amount above what is left.
              </p>
            ) : (
              <p className={hintClass} data-testid="refund-bounds">
                Paid: <Amount>{formatMinor(totalMinor, currency)}</Amount>.{" "}
                {remainingMinor === null ? null : (
                  <>
                    Left to refund: <Amount>{formatMinor(remainingMinor, currency)}</Amount>.
                  </>
                )}
              </p>
            )}
            <TextField
              id="refund-amount"
              label="Amount to refund (rupees)"
              hint="Type rupees, for example 1250 or 1250.50. A refund can be a part of the payment."
              value={amountText}
              inputMode="decimal"
              required
              disabled={locked}
              error={errors.amount}
              onChange={setAmountText}
            />
            {remainingMinor !== null && remainingMinor > 0 ? (
              <div>
                <Button
                  variant="outline"
                  disabled={locked}
                  onClick={() => setAmountText(minorToRupeeText(remainingMinor))}
                >
                  Refund everything that is left (
                  <Amount>{formatMinor(remainingMinor, currency)}</Amount>)
                </Button>
              </div>
            ) : null}
            <SelectField
              id="refund-reason-code"
              label="Why"
              placeholder="Choose a reason"
              options={refundReasonOptions}
              value={reasonCode}
              required
              disabled={locked}
              error={errors.reasonCode}
              onChange={setReasonCode}
            />
            <TextAreaField
              id="refund-reason"
              label="Reason"
              hint="Staff only. It goes into the history. The customer never sees it."
              value={reason}
              rows={3}
              maxLength={REFUND_REASON_MAX}
              required
              disabled={locked}
              error={errors.reason}
              onChange={setReason}
            />
          </>
        ) : (
          <fieldset className="m-0 grid min-w-0 gap-2 rounded-md border-2 border-destructive p-4 [&_p]:m-0">
            <legend className="sr-only">Confirm the refund</legend>
            <p className="text-lg">
              Refund{" "}
              <strong>
                <Amount>{formatMinor(amountMinor, currency)}</Amount>
              </strong>
              {totalMinor === null ? null : (
                <>
                  {" "}
                  of <Amount>{formatMinor(totalMinor, currency)}</Amount>
                </>
              )}
              ?
            </p>
            <p>
              This sends the money back to the customer through the payment provider.{" "}
              {evidence === "FAKE"
                ? "This is the fake gateway: no money moves."
                : "It cannot be undone."}
            </p>
            {locked ? (
              <p className={hintClass}>
                This refund was already sent once. Trying again sends the same refund: the provider
                is asked only once.
              </p>
            ) : null}
          </fieldset>
        )}
        {pending ? (
          <Notice tone="warning" role="status">
            <p>
              The provider has not answered yet. The refund may still be on its way. Check again in
              a moment: it is the same refund and it is never sent twice.
            </p>
          </Notice>
        ) : null}
        {failure === null ? null : (
          <PaymentFailureNotice
            failure={failure}
            hint={terminal ? "Close this window and read the payment again." : null}
          />
        )}
        <div className="flex flex-wrap gap-2">
          {terminal ? null : step === "form" ? (
            <Button type="submit" loading={inFlight}>
              Review the refund
            </Button>
          ) : (
            <>
              <Button variant="destructive" loading={inFlight} onClick={() => void send()}>
                {inFlight
                  ? "Sending…"
                  : failure?.retryable || pending
                    ? "Try again"
                    : `Refund ${formatMinor(amountMinor, currency)}`}
              </Button>
              <Button
                variant="secondary"
                disabled={inFlight || locked}
                onClick={() => setStep("form")}
              >
                Back
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={guardedClose} disabled={inFlight}>
            {terminal ? "Close" : "Cancel"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
